/**
 * Benefit Systems Integration Component
 * Manages integrations with HRIS/Benefit systems: Workday, ADP, SAP SuccessFactors, UKG
 */

import React, { useState } from 'react';
import {
  Building2,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  Play,
  Pause,
  RefreshCw,
  ChevronRight,
  FileSpreadsheet,
  Globe,
  Shield,
  X,
  Info,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Benefit system provider definitions
const BENEFIT_SYSTEMS = [
  {
    id: 'workday',
    name: 'Workday',
    logo: '/logos/workday.svg',
    description: 'Enterprise cloud applications for finance and HR',
    authType: 'oauth2',
    apiSupport: true,
    fileSupport: true,
    dataTypes: ['employees', 'benefits', 'enrollments', 'dependents', 'plans'],
    apiDocs: 'https://doc.workday.com/',
  },
  {
    id: 'adp',
    name: 'ADP Workforce Now',
    logo: '/logos/adp.svg',
    description: 'Human capital management and payroll services',
    authType: 'oauth2',
    apiSupport: true,
    fileSupport: true,
    dataTypes: ['employees', 'benefits', 'enrollments', 'deductions', 'plans'],
    apiDocs: 'https://developers.adp.com/',
  },
  {
    id: 'sap_successfactors',
    name: 'SAP SuccessFactors',
    logo: '/logos/sap.svg',
    description: 'Cloud-based HR software for core HR and talent management',
    authType: 'oauth2',
    apiSupport: true,
    fileSupport: true,
    dataTypes: ['employees', 'benefits', 'enrollments', 'compensation', 'plans'],
    apiDocs: 'https://help.sap.com/docs/SAP_SUCCESSFACTORS_PLATFORM',
  },
  {
    id: 'ukg',
    name: 'UKG Pro (Ultimate Kronos)',
    logo: '/logos/ukg.svg',
    description: 'HR, payroll, and workforce management solutions',
    authType: 'api_key',
    apiSupport: true,
    fileSupport: true,
    dataTypes: ['employees', 'benefits', 'enrollments', 'time_off', 'plans'],
    apiDocs: 'https://developer.ukg.com/',
  },
];

// Schedule frequency options
const SCHEDULE_OPTIONS = [
  { value: 'manual', label: 'Manual Only', description: 'Run on demand' },
  { value: 'hourly', label: 'Hourly', description: 'Every hour' },
  { value: 'daily', label: 'Daily', description: 'Once per day at specified time' },
  { value: 'weekly', label: 'Weekly', description: 'Once per week on specified day' },
  { value: 'monthly', label: 'Monthly', description: 'Once per month on specified day' },
];

// Data sync options - organized by category
const DATA_SYNC_OPTIONS = [
  // Core Employee Data
  { id: 'employees', label: 'Employee Demographics', description: 'Name, address, contact info, employment status', category: 'core' },
  { id: 'dependents', label: 'Dependents', description: 'Dependent information and coverage eligibility', category: 'core' },

  // Benefit Elections & Plans
  { id: 'benefit_elections', label: 'Benefit Elections', description: 'Medical, dental, vision plan selections and coverage levels', category: 'elections' },
  { id: 'enrollments', label: 'Enrollment Events', description: 'Open enrollment, qualifying life events, new hire enrollments', category: 'elections' },
  { id: 'plans', label: 'Plan Details & Comparison', description: 'Plan names, costs, coverage tiers, network details', category: 'elections' },
  { id: 'deductions', label: 'Payroll Deductions', description: 'Employee and employer contribution amounts', category: 'elections' },

  // Health Savings & Spending Accounts
  { id: 'hsa', label: 'Health Savings Account (HSA)', description: 'HSA balances, contributions, investment options', category: 'accounts' },
  { id: 'hsa_transactions', label: 'HSA Transactions & Usage', description: 'HSA claims, payments, eligible expenses tracking', category: 'accounts' },
  { id: 'fsa', label: 'Flexible Spending Account (FSA)', description: 'FSA balances, contributions, grace period status', category: 'accounts' },
  { id: 'fsa_transactions', label: 'FSA Transactions & Usage', description: 'FSA claims, reimbursements, eligible expenses', category: 'accounts' },
  { id: 'hra', label: 'Health Reimbursement Arrangement (HRA)', description: 'HRA balances and employer contributions', category: 'accounts' },
  { id: 'dcfsa', label: 'Dependent Care FSA', description: 'Dependent care FSA balances and usage', category: 'accounts' },

  // Pharmacy & Formulary
  { id: 'formulary', label: 'Drug Formulary', description: 'Covered medications, tiers, prior auth requirements', category: 'pharmacy' },
  { id: 'pharmacy_benefits', label: 'Pharmacy Benefits', description: 'Copays, coinsurance, specialty drug coverage', category: 'pharmacy' },
  { id: 'pharmacy_claims', label: 'Pharmacy Claims History', description: 'Prescription fills, costs, pharmacy locations', category: 'pharmacy' },

  // Additional Benefits
  { id: 'life_insurance', label: 'Life & AD&D Insurance', description: 'Life insurance elections, beneficiaries, coverage amounts', category: 'additional' },
  { id: 'disability', label: 'Disability Insurance', description: 'Short-term and long-term disability coverage', category: 'additional' },
  { id: 'retirement', label: '401(k) / Retirement Plans', description: 'Retirement plan contributions and vesting', category: 'additional' },
];

interface IntegrationConfig {
  systemId: string;
  name: string;
  connectionType: 'api' | 'file';
  isActive: boolean;
  schedule: string;
  scheduleTime?: string;
  scheduleDay?: string;
  lastSync?: string;
  lastStatus?: 'success' | 'error' | 'running';
  syncOptions: string[];
  credentials?: {
    clientId?: string;
    clientSecret?: string;
    apiKey?: string;
    tenantId?: string;
    endpoint?: string;
  };
}

export const BenefitSystemsIntegration: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [showConfigWizard, setShowConfigWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [configuredIntegrations, setConfiguredIntegrations] = useState<IntegrationConfig[]>([]);
  const [currentConfig, setCurrentConfig] = useState<Partial<IntegrationConfig>>({});

  // Start configuration wizard for a system
  const startConfiguration = (systemId: string) => {
    setSelectedSystem(systemId);
    setCurrentConfig({
      systemId,
      name: BENEFIT_SYSTEMS.find(s => s.id === systemId)?.name || '',
      connectionType: 'api',
      isActive: false,
      schedule: 'daily',
      scheduleTime: '02:00',
      syncOptions: ['employees', 'dependents', 'benefit_elections', 'enrollments', 'plans', 'hsa', 'fsa', 'formulary', 'life_insurance'],
      credentials: {},
    });
    setWizardStep(1);
    setShowConfigWizard(true);
  };

  // Group data sync options by category
  const DATA_SYNC_CATEGORIES = [
    { id: 'core', label: 'Core Employee Data', icon: '👤' },
    { id: 'elections', label: 'Benefit Elections & Plans', icon: '📋' },
    { id: 'accounts', label: 'Health & Spending Accounts', icon: '💰' },
    { id: 'pharmacy', label: 'Pharmacy & Formulary', icon: '💊' },
    { id: 'additional', label: 'Additional Benefits', icon: '🛡️' },
  ];

  // Save integration configuration
  const saveConfiguration = () => {
    if (currentConfig.systemId) {
      const existingIndex = configuredIntegrations.findIndex(i => i.systemId === currentConfig.systemId);
      if (existingIndex >= 0) {
        const updated = [...configuredIntegrations];
        updated[existingIndex] = currentConfig as IntegrationConfig;
        setConfiguredIntegrations(updated);
      } else {
        setConfiguredIntegrations([...configuredIntegrations, currentConfig as IntegrationConfig]);
      }
    }
    setShowConfigWizard(false);
    setSelectedSystem(null);
    setCurrentConfig({});
  };

  // Toggle integration active state
  const toggleIntegration = (systemId: string) => {
    setConfiguredIntegrations(prev =>
      prev.map(i => i.systemId === systemId ? { ...i, isActive: !i.isActive } : i)
    );
  };

  // Run manual sync
  const runSync = (systemId: string) => {
    setConfiguredIntegrations(prev =>
      prev.map(i => i.systemId === systemId ? { ...i, lastStatus: 'running' } : i)
    );
    // Simulate sync completion
    setTimeout(() => {
      setConfiguredIntegrations(prev =>
        prev.map(i => i.systemId === systemId ? {
          ...i,
          lastStatus: 'success',
          lastSync: new Date().toISOString()
        } : i)
      );
    }, 3000);
  };

  const selectedSystemData = BENEFIT_SYSTEMS.find(s => s.id === selectedSystem);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('integrations.benefitSystems', 'Benefit Systems Integration')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('integrations.benefitSystemsDescription', 'Connect to HRIS and benefit administration systems for automated data synchronization')}
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">
              {t('integrations.apiAndFileSupport', 'API & File Import Support')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {t('integrations.apiFileDescription', 'All benefit systems support both real-time API connections and file-based imports (CSV, Excel, JSON). Use API for real-time sync and file imports for bulk data loads or initial migrations.')}
            </p>
          </div>
        </div>
      </div>

      {/* Configured Integrations */}
      {configuredIntegrations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {t('integrations.configuredIntegrations', 'Configured Integrations')}
          </h3>
          <div className="space-y-4">
            {configuredIntegrations.map(integration => {
              const system = BENEFIT_SYSTEMS.find(s => s.id === integration.systemId);
              return (
                <div
                  key={integration.systemId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                          {system?.name}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            {integration.connectionType === 'api' ? (
                              <Globe className="h-4 w-4" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4" />
                            )}
                            {integration.connectionType === 'api' ? 'API' : 'File Import'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {SCHEDULE_OPTIONS.find(s => s.value === integration.schedule)?.label}
                          </span>
                          {integration.lastSync && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              Last: {fmt.dateTime(integration.lastSync)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {integration.lastStatus === 'running' ? (
                        <span className="flex items-center gap-1 text-blue-600">
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Syncing...
                        </span>
                      ) : integration.lastStatus === 'success' ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Success
                        </span>
                      ) : integration.lastStatus === 'error' ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          Error
                        </span>
                      ) : null}
                      <button
                        onClick={() => runSync(integration.systemId)}
                        disabled={integration.lastStatus === 'running'}
                        className="p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                        title="Run Sync Now"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleIntegration(integration.systemId)}
                        className={`p-2 rounded-lg transition-colors ${
                          integration.isActive
                            ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={integration.isActive ? 'Pause Integration' : 'Enable Integration'}
                      >
                        {integration.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => startConfiguration(integration.systemId)}
                        className="p-2 text-gray-600 hover:text-teal-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Configure"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Systems */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {t('integrations.availableSystems', 'Available Benefit Systems')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BENEFIT_SYSTEMS.map(system => {
            const isConfigured = configuredIntegrations.some(i => i.systemId === system.id);
            return (
              <div
                key={system.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {system.name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {system.description}
                      </p>
                    </div>
                  </div>
                  {isConfigured && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                      Configured
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                    <Globe className="h-3 w-3" />
                    API
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">
                    <FileSpreadsheet className="h-3 w-3" />
                    CSV/Excel
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                    <Shield className="h-3 w-3" />
                    {system.authType === 'oauth2' ? 'OAuth 2.0' : 'API Key'}
                  </span>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <strong>Data Types:</strong> {system.dataTypes.join(', ')}
                </div>

                <button
                  onClick={() => startConfiguration(system.id)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  {isConfigured ? 'Edit Configuration' : 'Configure Integration'}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Configuration Wizard Modal */}
      {showConfigWizard && selectedSystemData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Configure {selectedSystemData.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Step {wizardStep} of 4
                </p>
              </div>
              <button
                onClick={() => setShowConfigWizard(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 pt-4">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4].map(step => (
                  <div key={step} className="flex-1">
                    <div
                      className={`h-2 rounded-full ${
                        step <= wizardStep
                          ? 'bg-teal-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Connection</span>
                <span>Authentication</span>
                <span>Data Options</span>
                <span>Schedule</span>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Step 1: Connection Type */}
              {wizardStep === 1 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Choose Connection Type
                  </h4>
                  <div className="space-y-4">
                    <button
                      onClick={() => setCurrentConfig({ ...currentConfig, connectionType: 'api' })}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        currentConfig.connectionType === 'api'
                          ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Globe className="h-6 w-6 text-teal-600" />
                        <div>
                          <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                            API Connection
                          </h5>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Real-time data synchronization via REST API. Supports automatic scheduling and incremental updates.
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => setCurrentConfig({ ...currentConfig, connectionType: 'file' })}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        currentConfig.connectionType === 'file'
                          ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="h-6 w-6 text-purple-600" />
                        <div>
                          <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                            File Import
                          </h5>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Upload CSV, Excel, or JSON files manually or via SFTP. Best for bulk data loads and migrations.
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Authentication */}
              {wizardStep === 2 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    {currentConfig.connectionType === 'api' ? 'API Credentials' : 'File Source Configuration'}
                  </h4>
                  {currentConfig.connectionType === 'api' ? (
                    <div className="space-y-4">
                      {selectedSystemData.authType === 'oauth2' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Client ID
                            </label>
                            <input
                              type="text"
                              value={currentConfig.credentials?.clientId || ''}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig,
                                credentials: { ...currentConfig.credentials, clientId: e.target.value }
                              })}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Enter OAuth Client ID"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Client Secret
                            </label>
                            <input
                              type="password"
                              value={currentConfig.credentials?.clientSecret || ''}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig,
                                credentials: { ...currentConfig.credentials, clientSecret: e.target.value }
                              })}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Enter OAuth Client Secret"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Tenant ID / Instance URL
                            </label>
                            <input
                              type="text"
                              value={currentConfig.credentials?.tenantId || ''}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig,
                                credentials: { ...currentConfig.credentials, tenantId: e.target.value }
                              })}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="e.g., your-company.workday.com"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              API Key
                            </label>
                            <input
                              type="password"
                              value={currentConfig.credentials?.apiKey || ''}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig,
                                credentials: { ...currentConfig.credentials, apiKey: e.target.value }
                              })}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Enter API Key"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              API Endpoint URL
                            </label>
                            <input
                              type="text"
                              value={currentConfig.credentials?.endpoint || ''}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig,
                                credentials: { ...currentConfig.credentials, endpoint: e.target.value }
                              })}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="https://api.example.com/v1"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          For file imports, you can upload files directly through the Integration Workbench or configure an SFTP endpoint for automated file retrieval.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          SFTP Host (Optional)
                        </label>
                        <input
                          type="text"
                          value={currentConfig.credentials?.endpoint || ''}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig,
                            credentials: { ...currentConfig.credentials, endpoint: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="sftp.example.com"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Data Options */}
              {wizardStep === 3 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Select Data to Synchronize
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Choose which data types to sync from your benefit system. You can select multiple options.
                  </p>
                  <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                    {DATA_SYNC_CATEGORIES.map(category => {
                      const categoryOptions = DATA_SYNC_OPTIONS.filter(opt => opt.category === category.id);
                      const selectedCount = categoryOptions.filter(opt => currentConfig.syncOptions?.includes(opt.id)).length;
                      return (
                        <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 flex items-center justify-between">
                            <span className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                              <span>{category.icon}</span>
                              {category.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {selectedCount}/{categoryOptions.length} selected
                            </span>
                          </div>
                          <div className="p-3 space-y-2">
                            {categoryOptions.map(option => (
                              <label
                                key={option.id}
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  currentConfig.syncOptions?.includes(option.id)
                                    ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-teal-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={currentConfig.syncOptions?.includes(option.id) || false}
                                  onChange={(e) => {
                                    const current = currentConfig.syncOptions || [];
                                    if (e.target.checked) {
                                      setCurrentConfig({ ...currentConfig, syncOptions: [...current, option.id] });
                                    } else {
                                      setCurrentConfig({ ...currentConfig, syncOptions: current.filter(id => id !== option.id) });
                                    }
                                  }}
                                  className="mt-0.5 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                />
                                <div>
                                  <h5 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                    {option.label}
                                  </h5>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {option.description}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 4: Schedule */}
              {wizardStep === 4 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Configure Sync Schedule
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sync Frequency
                      </label>
                      <div className="space-y-2">
                        {SCHEDULE_OPTIONS.map(option => (
                          <label
                            key={option.value}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              currentConfig.schedule === option.value
                                ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-teal-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="schedule"
                              value={option.value}
                              checked={currentConfig.schedule === option.value}
                              onChange={(e) => setCurrentConfig({ ...currentConfig, schedule: e.target.value })}
                              className="h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {option.label}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                                - {option.description}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {currentConfig.schedule && currentConfig.schedule !== 'manual' && currentConfig.schedule !== 'hourly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sync Time
                        </label>
                        <input
                          type="time"
                          value={currentConfig.scheduleTime || '02:00'}
                          onChange={(e) => setCurrentConfig({ ...currentConfig, scheduleTime: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Recommended: Schedule during off-peak hours (e.g., 2:00 AM)
                        </p>
                      </div>
                    )}

                    {currentConfig.schedule === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Day of Week
                        </label>
                        <select
                          value={currentConfig.scheduleDay || 'monday'}
                          onChange={(e) => setCurrentConfig({ ...currentConfig, scheduleDay: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    )}

                    {currentConfig.schedule === 'monthly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Day of Month
                        </label>
                        <select
                          value={currentConfig.scheduleDay || '1'}
                          onChange={(e) => setCurrentConfig({ ...currentConfig, scheduleDay: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          {[...Array(28)].map((_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  if (wizardStep > 1) {
                    setWizardStep(wizardStep - 1);
                  } else {
                    setShowConfigWizard(false);
                  }
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                {wizardStep === 1 ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={() => {
                  if (wizardStep < 4) {
                    setWizardStep(wizardStep + 1);
                  } else {
                    saveConfiguration();
                  }
                }}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                {wizardStep === 4 ? 'Save Configuration' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
          {t('integrations.howBenefitIntegrationsWork', 'How Benefit System Integrations Work')}
        </h4>
        <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
          <li>Select a benefit system (Workday, ADP, SAP SuccessFactors, or UKG)</li>
          <li>Choose connection type: API for real-time sync or file import for bulk loads</li>
          <li>Configure authentication credentials or file source</li>
          <li>Select which data types to synchronize (employees, benefits, enrollments, etc.)</li>
          <li>Set up automatic scheduling or run manual syncs as needed</li>
          <li>Monitor sync status and review any errors in the Monitoring tab</li>
        </ol>
      </div>
    </div>
  );
};

export default BenefitSystemsIntegration;
